import { usePreferences } from "../../lib/preferences";
import { Icon, type IconName } from "./Icon";
import type { ThemePreference } from "@vedaunion/shared";

/**
 * Theme control. Two shapes from one source of truth (usePreferences):
 *  - `variant="button"` (default): a compact icon button that cycles
 *    light → dark → system, for the platform header.
 *  - `variant="segmented"`: a labelled 3-way control for the preferences panel.
 * The choice persists to the account (logged in) or localStorage (guest).
 */

const ORDER: ThemePreference[] = ["light", "dark", "system"];
const META: Record<ThemePreference, { icon: IconName; label: string }> = {
  light: { icon: "sun", label: "Light" },
  dark: { icon: "moon", label: "Dark" },
  system: { icon: "monitor", label: "System" },
};

export function ThemeToggle({ variant = "button" }: { variant?: "button" | "segmented" }) {
  const { prefs, update } = usePreferences();
  const current = prefs.theme;

  if (variant === "segmented") {
    return (
      <div className="theme-seg" role="group" aria-label="Colour theme">
        {ORDER.map((t) => (
          <button
            key={t}
            type="button"
            className="theme-seg-opt"
            data-active={current === t}
            aria-pressed={current === t}
            onClick={() => update({ theme: t })}
          >
            <Icon name={META[t].icon} size="1.05em" />
            <span>{META[t].label}</span>
          </button>
        ))}
      </div>
    );
  }

  const next = ORDER[(ORDER.indexOf(current) + 1) % ORDER.length]!;
  return (
    <button
      type="button"
      className="theme-toggle-btn"
      onClick={() => update({ theme: next })}
      aria-label={`Theme: ${META[current].label}. Switch to ${META[next].label}.`}
      title={`Theme: ${META[current].label} — click for ${META[next].label}`}
    >
      <Icon name={META[current].icon} size="1.15rem" />
    </button>
  );
}
